<script lang="ts" module>
  import "simplebar"
  import "simplebar/dist/simplebar.css"
</script>

<script lang="ts">
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import { mdiChevronLeft, mdiChevronRight } from "@mdi/js"
  import { onDestroy } from "svelte"
  import SimpleBar from "simplebar"

  interface Props {
    placement?: "top" | "bottom"
    contentWidth: number
    scrollLeft: number
    canScrollLeft: boolean
    canScrollRight: boolean
    onScrollTo?: (value: number) => void
    onStep?: (delta: number) => void
  }

  let {
    placement = "top",
    contentWidth = 0,
    scrollLeft = 0,
    canScrollLeft = false,
    canScrollRight = false,
    onScrollTo = () => {},
    onStep = () => {},
  }: Props = $props()

  let scrollbarEl: HTMLDivElement | null = $state(null)
  let scrollElement: HTMLElement | null = $state(null)
  let simplebar: SimpleBar | null = null

  const destroySimplebar = () => {
    simplebar?.unMount()
    simplebar = null
    scrollElement = null
  }

  $effect(() => {
    if (typeof window === "undefined") return

    contentWidth
    const host = scrollbarEl

    if (!host) return

    simplebar = SimpleBar.instances.get(host) ?? new SimpleBar(host, { autoHide: false })
    simplebar.recalculate()
    const target = simplebar.getScrollElement()
    scrollElement = target

    const handleScroll = () => onScrollTo(target.scrollLeft)
    target.addEventListener("scroll", handleScroll, { passive: true })

    return () => target.removeEventListener("scroll", handleScroll)
  })

  $effect(() => {
    const target = scrollElement

    if (!target) return

    if (Math.abs(target.scrollLeft - scrollLeft) > 1) {
      target.scrollLeft = scrollLeft
    }
  })

  onDestroy(destroySimplebar)
</script>

<div
  class="board-horizontal-controls"
  class:top={placement === "top"}
  class:bottom={placement === "bottom"}
>
  <button
    type="button"
    class="board-horizontal-step-button"
    aria-label="Horizontal nach links scrollen"
    disabled={!canScrollLeft}
    onclick={() => onStep(-1)}
  >
    <Icon path={mdiChevronLeft} />
  </button>
  <div
    class="board-horizontal-scrollbar"
    data-simplebar
    data-simplebar-auto-hide="false"
    bind:this={scrollbarEl}
  >
    <div class="board-horizontal-scrollbar__content" style={`width:${Math.max(contentWidth, 1)}px;`}
    ></div>
  </div>
  <button
    type="button"
    class="board-horizontal-step-button"
    aria-label="Horizontal nach rechts scrollen"
    disabled={!canScrollRight}
    onclick={() => onStep(1)}
  >
    <Icon path={mdiChevronRight} />
  </button>
</div>

<style lang="less">
  .board-horizontal-controls {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 0.4rem;
    background-color: var(--cal-surface);
    border-left: 1px solid var(--cal-border);
    border-right: 1px solid var(--cal-border);
    padding: 0.25rem 0.4rem;

    &.top {
      border-top: 1px solid var(--cal-border);
    }

    &.bottom {
      border-bottom: 1px solid var(--cal-border);
    }
  }

  .board-horizontal-step-button {
    flex: 0 0 auto;
    border: none;
    background: transparent;
    color: var(--cal-text-muted);
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: 0;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    transition:
      background-color 0.1s ease,
      color 0.1s ease,
      opacity 0.1s ease;

    &:hover:not(:disabled) {
      color: var(--cal-accent-soft);
      background-color: rgba(116, 30, 32, 0.12);
    }

    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
  }

  .board-horizontal-scrollbar {
    flex: 1 1 auto;
    min-width: 0;
    box-sizing: border-box;
    height: 19px;
    background-color: var(--cal-scrollbar-surface, var(--cal-surface));

    &__content {
      height: 2px;
    }

    :global(.simplebar-track.simplebar-vertical) {
      display: none;
    }

    :global(.simplebar-wrapper),
    :global(.simplebar-mask),
    :global(.simplebar-offset),
    :global(.simplebar-content-wrapper),
    :global(.simplebar-content) {
      height: 100%;
    }

    :global(.simplebar-track.simplebar-horizontal) {
      width: auto !important;
      height: 16px !important;
      top: auto !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      margin: 0 !important;
      overflow: visible !important;
      background-color: var(--cal-scrollbar-track, rgba(13, 12, 12, 0.25)) !important;
      box-shadow:
        inset 0 2px 0 var(--cal-scrollbar-surface, var(--cal-surface)),
        inset 0 -2px 0 var(--cal-scrollbar-surface, var(--cal-surface));
      pointer-events: auto;
      cursor: pointer;
    }

    :global(.simplebar-track.simplebar-horizontal .simplebar-scrollbar) {
      height: 16px !important;
      min-width: 40px;
      pointer-events: auto;
      cursor: grab;
    }

    :global(.simplebar-track.simplebar-horizontal .simplebar-scrollbar::before) {
      background-color: var(--cal-scrollbar-thumb, #0d0c0c) !important;
      opacity: 1 !important;
      top: -1px !important;
      right: -2px !important;
      bottom: -1px !important;
      left: -2px !important;
      width: auto !important;
      height: auto !important;
      margin: 0 !important;
      border-radius: 0;
      transition-delay: 0s;
      transition-duration: 0ms;
    }

    :global(.simplebar-track.simplebar-horizontal .simplebar-scrollbar:active) {
      cursor: grabbing;
    }

    :global(.simplebar-track.simplebar-horizontal .simplebar-scrollbar.simplebar-visible::before) {
      opacity: 1 !important;
    }

    :global(.simplebar-content-wrapper) {
      overflow-x: auto !important;
      overflow-y: hidden !important;
      scroll-behavior: auto !important;
    }
  }
</style>
